import * as React from "react";
import { Alert, Box, FormControlLabel, MenuItem, Stack, Switch, TextField, Typography } from "@mui/material";
import type { LlmCostEstimates, LlmSettings, ReasoningEffort } from "../../models/domain";
import AgentCard from "./AgentCard";
import BasePromptPanel from "./BasePromptPanel";
import ModelPicker from "./ModelPicker";
import { formatPerThousand, formatUsd, roleCost } from "./costs";
import { REASONING_EFFORTS, acceptsTemperature, capsOf, fallbackCandidates, fallbackProblem, usesRealtime } from "./llmCatalog";
import type { RolePrompts } from "./rolePrompts";
import {
  audioFormatsFor,
  globalRoleChanged,
  ownValue,
  reviewEnabled,
  setValue,
  validVoice,
  type LlmField,
  type RoleMeta,
} from "./llmSettings";

/** Température de départ d'un rôle qui en règle une (générateur : tc_learn `BOOT_TEMPERATURE` ; compagnon : 0,4). */
const DEFAULT_TEMPERATURE = 0.4;

const UNIT_LABEL = {
  lesson: "par leçon",
  objective: "par objectif extrait",
  served_quest: "par quête servie",
  companion_reply: "par réponse",
} as const;
const UNITS_LABEL = {
  lesson: "leçons traitées",
  objective: "objectifs extraits",
  served_quest: "quêtes servies",
  companion_reply: "réponses",
} as const;

/** L'unité d'un rôle avant que la mesure n'arrive (même règle que le serveur). */
const unitOf = (role: RoleMeta["role"]) =>
  role === "objective_extraction"
    ? "lesson"
    : role.startsWith("fact_")
      ? "objective"
      : role === "companion"
        ? "companion_reply"
        : "served_quest";

/** Le coût d'une unité avec le couple choisi, et d'où vient la mesure. */
const CostLine: React.FC<{
  meta: RoleMeta;
  provider: string;
  model: string;
  estimates: LlmCostEstimates | null;
  off: boolean;
  error?: string | null;
}> = ({ meta, provider, model, estimates, off, error }) => {
  const measure = estimates?.roles[meta.role];
  const cost = roleCost(estimates, meta.role, provider, model);
  const unit = measure?.unit ?? unitOf(meta.role);
  return (
    <Box>
      <Typography variant="subtitle2">Coût estimé</Typography>
      {off ? (
        <Typography variant="body2">0 $ tant que la relecture est coupée.</Typography>
      ) : cost ? (
        <>
          <Typography variant="body2">
            <b>≈ {formatUsd(cost.usd)}</b> {UNIT_LABEL[unit]} · {formatPerThousand(cost.usd)}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            Consommation mesurée sur {estimates?.window_days} jours ({measure?.units.toLocaleString("fr-FR")} {UNITS_LABEL[unit]},{" "}
            {measure?.calls.toLocaleString("fr-FR")} appels) × tarif de {model}.
            {!cost.measured && " Ce modèle a moins de 50 appels de ce rôle : ses jetons sont ceux des autres modèles, un ordre de grandeur."}
          </Typography>
        </>
      ) : (
        <Typography variant="caption" color="text.secondary" component="div">
          {error ??
            (!estimates
              ? "Estimation en cours de chargement."
              : unit === "lesson" && !measure?.units
                ? "Pas encore de mesure : les leçons se comptent à partir du premier chapitre extrait depuis la mise en service de ce réglage."
                : unit === "companion_reply" && !measure?.units
                  ? "Pas encore de mesure : les réponses se comptent dès que leurs appels portent le rôle du compagnon (tc_ecosys 0134)."
                  : "Pas de mesure pour ce modèle sur la fenêtre (aucun appel, ou modèle sans tarif).")}
        </Typography>
      )}
    </Box>
  );
};

interface Props {
  meta: RoleMeta;
  icon: React.ReactNode;
  llm: LlmSettings;
  saved: LlmSettings;
  onChange: (llm: LlmSettings) => void;
  /** Coupure et rétablissement d'une relecture : la page confirme une coupure. */
  onToggle: (enabled: boolean) => void;
  /** Le modèle tapé n'est pas déclaré : la page ouvre « Déclarer un modèle ». */
  onDeclare: (provider: string, model: string) => void;
  estimates: LlmCostEstimates | null;
  estimatesError?: string | null;
  prompts: RolePrompts | null;
  disabled?: boolean;
}

/**
 * La carte d'un agent au GLOBAL : fournisseur et modèle, température et effort
 * de raisonnement selon ce que le modèle accepte, interrupteur des relectures,
 * coût estimé, consigne globale et prompt de base qu'elle complète. Les écarts
 * par matière sont dans le tableau sous les cartes.
 */
export const RoleAgentCard: React.FC<Props> = ({
  meta,
  icon,
  llm,
  saved,
  onChange,
  onToggle,
  onDeclare,
  estimates,
  estimatesError,
  prompts,
  disabled,
}) => {
  const set = (field: LlmField, v: string | number | undefined) => onChange(setValue(llm, "", meta.role, field, v));
  const provider = (ownValue(llm, "", meta.role, "provider") as string | undefined) ?? "";
  const model = (ownValue(llm, "", meta.role, "model") as string | undefined) ?? "";
  const caps = capsOf(llm, provider, model);
  const effort = (ownValue(llm, "", meta.role, "reasoning_effort") as ReasoningEffort | undefined) ?? "";
  const instructions = (ownValue(llm, "", meta.role, "instructions") as string | undefined) ?? "";
  // Le texte TAPÉ : le réglage est nettoyé (espaces de bord retirés), et le
  // réafficher tel quel avalerait l'espace qu'on vient de taper. On ne le
  // recale que si le réglage change par ailleurs (Annuler, rechargement).
  const [draft, setDraft] = React.useState(instructions);
  React.useEffect(() => {
    setDraft((d) => (d.trim() === instructions ? d : instructions));
  }, [instructions]);
  const enabled = meta.switchOff ? reviewEnabled(llm, meta.role) : true;
  const voice = (ownValue(llm, "", meta.role, "voice") as string | undefined) ?? "";
  const audioFormat = (ownValue(llm, "", meta.role, "audio_format") as string | undefined) ?? "";
  const fallback = (ownValue(llm, "", meta.role, "fallback_model") as string | undefined) ?? "";
  const fallbacks = meta.fallback ? fallbackCandidates(llm, provider, model) : [];
  const fallbackError = meta.fallback ? fallbackProblem(llm, provider, model, fallback) : null;
  const realtime = meta.voiceFields ? usesRealtime(llm, provider, model, fallback) : false;
  const formats = audioFormatsFor(realtime);
  // Un format déjà posé hors de la liste (passage à un modèle temps réel) reste
  // affiché, signalé en erreur : le faire disparaître masquerait ce que le
  // serveur refusera à l'enregistrement.
  const formatRefused = audioFormat !== "" && !(formats as string[]).includes(audioFormat);

  return (
    <AgentCard
      icon={icon}
      title={meta.label}
      role={meta.hint}
      modelHint={meta.modelHint}
      modelControl={
        <ModelPicker
          role={meta.role}
          llm={llm}
          provider={provider}
          model={model}
          onProvider={(v) => set("provider", v)}
          onModel={(v) => set("model", v)}
          onDeclare={onDeclare}
          label={meta.label}
          disabled={disabled}
        />
      }
      temperature={
        meta.temperature && acceptsTemperature(caps)
          ? ((ownValue(llm, "", meta.role, "temperature") as number | undefined) ?? DEFAULT_TEMPERATURE)
          : undefined
      }
      onTemperature={meta.temperature && acceptsTemperature(caps) ? (t) => set("temperature", t) : undefined}
      defaultTemperature={meta.temperature ? DEFAULT_TEMPERATURE : undefined}
      temperatureNote={
        meta.temperature && caps && !acceptsTemperature(caps)
          ? "Ce modèle n'accepte pas de température."
          : meta.temperatureNote
      }
      dirty={globalRoleChanged(llm, saved, meta.role)}
      footnote={meta.footnote}
      disabled={disabled}
    >
      {caps?.reasoning && (
        <Box>
          <Typography variant="subtitle2">Effort de raisonnement</Typography>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
            Plus d'effort : réponses plus sûres, plus lentes, plus de jetons de sortie payés. Vide = l'effort par défaut
            déclaré pour le modèle{caps.default_effort ? ` (${caps.default_effort})` : ""}.
          </Typography>
          <TextField
            select
            size="small"
            disabled={disabled}
            value={effort}
            onChange={(e) => set("reasoning_effort", e.target.value || undefined)}
            SelectProps={{ displayEmpty: true }}
            inputProps={{ "aria-label": `Effort de raisonnement — ${meta.label}` }}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">par défaut du modèle</MenuItem>
            {REASONING_EFFORTS.map((e) => (
              <MenuItem key={e} value={e}>
                {e}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}
      {meta.voiceFields && (
        <Box>
          <Typography variant="subtitle2">Voix et format</Typography>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
            Obligatoires. La voix est un nom du fournisseur ; le format est celui du fichier envoyé au navigateur de
            l'élève.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              size="small"
              label="Voix"
              disabled={disabled}
              value={voice}
              onChange={(e) => set("voice", e.target.value)}
              error={voice === "" || !validVoice(voice)}
              helperText={
                voice === ""
                  ? "Obligatoire, ex. marin, cedar."
                  : validVoice(voice)
                    ? "Ex. marin, cedar."
                    : "Minuscules, chiffres, - et _ (40 caractères au plus)."
              }
              inputProps={{ "aria-label": `Voix — ${meta.label}` }}
              sx={{ minWidth: 200 }}
            />
            <TextField
              select
              size="small"
              label="Format audio"
              disabled={disabled}
              value={audioFormat}
              onChange={(e) => set("audio_format", e.target.value)}
              error={audioFormat === "" || formatRefused}
              helperText={
                audioFormat === ""
                  ? "Obligatoire."
                  : formatRefused
                    ? "Temps réel : mp3 ou wav seulement."
                    : realtime
                      ? "Temps réel : mp3 ou wav."
                      : " "
              }
              inputProps={{ "aria-label": `Format audio — ${meta.label}` }}
              sx={{ minWidth: 160 }}
            >
              {formats.map((f) => (
                <MenuItem key={f} value={f}>
                  {f}
                </MenuItem>
              ))}
              {formatRefused && (
                <MenuItem value={audioFormat} disabled>
                  {audioFormat}
                </MenuItem>
              )}
            </TextField>
          </Stack>
        </Box>
      )}
      {meta.fallback && (
        <Box>
          <Typography variant="subtitle2">Modèle de repli</Typography>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
            Utilisé si le modèle principal échoue avant le premier son (erreur, ou 15 s sans son) : l'élève entend
            quand même la lecture. Un autre modèle de synthèse vocale du même fournisseur. À vider avant le retrait de
            l'ancien modèle (gpt-4o-mini-tts, le 06/01/2027).
          </Typography>
          <TextField
            select
            size="small"
            disabled={disabled || !provider}
            value={fallback}
            onChange={(e) => set("fallback_model", e.target.value || undefined)}
            SelectProps={{ displayEmpty: true }}
            error={Boolean(fallbackError)}
            helperText={fallbackError ?? (fallbacks.length ? " " : "Aucun autre modèle de synthèse vocale déclaré chez ce fournisseur.")}
            inputProps={{ "aria-label": `Modèle de repli — ${meta.label}` }}
            sx={{ minWidth: 240 }}
          >
            <MenuItem value="">aucun repli</MenuItem>
            {fallbacks.map((m) => (
              <MenuItem key={m} value={m}>
                {m}
                {capsOf(llm, provider, m)?.realtime ? " (temps réel)" : ""}
              </MenuItem>
            ))}
            {fallback !== "" && !fallbacks.includes(fallback) && (
              <MenuItem value={fallback} disabled>
                {fallback}
              </MenuItem>
            )}
          </TextField>
        </Box>
      )}
      {meta.switchOff && (
        <Box>
          <FormControlLabel
            control={
              <Switch
                color={enabled ? "primary" : "error"}
                checked={enabled}
                disabled={disabled}
                onChange={(e) => onToggle(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2" color={enabled ? undefined : "error"}>
                {meta.label} {enabled ? "active" : "coupée"}
              </Typography>
            }
          />
          {!enabled && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {meta.switchOff}
            </Alert>
          )}
        </Box>
      )}

      {!meta.noEstimate && (
        <CostLine meta={meta} provider={provider} model={model} estimates={estimates} off={!enabled} error={estimatesError} />
      )}

      {meta.instructions && (
        <Box>
          <Typography variant="subtitle2">Consigne globale</Typography>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
            Ajoutée à la fin du message, après le prompt de base, à chaque appel. Une matière qui a sa propre consigne
            l'utilise à la place de celle-ci.
          </Typography>
          <TextField
            multiline
            minRows={3}
            fullWidth
            disabled={disabled}
            value={draft}
            placeholder="Aucune consigne : le prompt de base seul."
            onChange={(e) => {
              setDraft(e.target.value);
              set("instructions", e.target.value);
            }}
            inputProps={{ maxLength: 2000, "aria-label": `Consigne globale — ${meta.label}` }}
            helperText={`${draft.length} / 2000`}
          />
        </Box>
      )}

      {prompts && (
        <BasePromptPanel
          version={prompts.version}
          assembly={prompts.assembly}
          groups={prompts.groups}
          instructionsHeader={meta.instructions ? prompts.instructionsHeader : undefined}
          instructionsNote="la consigne de la matière si elle en a une, sinon la consigne globale."
          error={prompts.error}
        />
      )}
    </AgentCard>
  );
};

export default RoleAgentCard;
